import { FunctionComponent } from 'react'
import {
  ClientPortfolioSortBy,
  ProjectSummary,
} from '@black-bear-energy/black-bear-energy-common'
import { ErrorOutline } from '@mui/icons-material'
import {
  Box,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  rucksack,
} from '@black-bear-energy/rucksack'
import emptyMap from '../../assets/empty-map.svg'
import houseSearch from '../../assets/house-search.svg'
import { SearchBar } from '../common/SearchBar'
import { Surface } from '../common/Surface'
import { PROJECT_SUMMARY_COLUMNS } from '../helpers/Dashboard/ProjectsTable/tableConstants'
import { CustomerRFPRow } from './CustomerRFPRow'
import { SortButton } from '../common/SortButton'

const defaultTableHeight = '39.5rem'

const SORT_BY_DISPLAY_NAMES: Record<ClientPortfolioSortBy, string> = {
  [ClientPortfolioSortBy.Name]: 'Name',
  [ClientPortfolioSortBy.SystemSize]: 'Size',
}

export const ProjectsTable: FunctionComponent<ProjectsTableProps> = ({
  records,
  isPending,
  isError,
  sortBy,
  setSortBy,
  sortAsc,
  setSortAsc,
  search,
  setSearch,
  page,
  setPage,
  totalPages,
}) => {
  return (
    <Surface>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="h6">Projects</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '220px' }}>
              <SearchBar
                label="Search"
                search={search ?? ''}
                setSearch={setSearch}
              />
            </Box>
            <SortButton
              sortBy={sortBy}
              displayNames={SORT_BY_DISPLAY_NAMES}
              setSortBy={setSortBy}
              sortAsc={sortAsc}
              setSortAsc={setSortAsc}
              handleReset={() => {
                setSortBy(ClientPortfolioSortBy.Name)
                setSortAsc(true)
              }}
              resetDisabled={sortBy === ClientPortfolioSortBy.Name && sortAsc}
              showSortByLabel={false}
            />
          </Box>
        </Box>
        {isPending ? (
          <Skeleton variant="rounded" height={defaultTableHeight} />
        ) : (
          <Box sx={{ minHeight: defaultTableHeight }}>
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed', minWidth: '800px' }}>
                <TableHead
                  sx={{
                    '& .MuiTableCell-head': {
                      fontSize: '14px',
                      padding: '6px 16px',
                    },
                  }}
                >
                  <TableRow>
                    {PROJECT_SUMMARY_COLUMNS.map((column) => (
                      <TableCell
                        key={column.title}
                        sx={{
                          width: `${column.width}%`,
                          backgroundColor: rucksack.palette.background.card,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {column.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isError ? (
                    <TableRow>
                      <TableCell
                        colSpan={PROJECT_SUMMARY_COLUMNS.length}
                        sx={{ border: 0, height: defaultTableHeight }}
                      >
                        <Box
                          role="alert"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            height: '100%',
                            color: 'error.main',
                          }}
                        >
                          <ErrorOutline fontSize="small" />
                          <Typography
                            variant="body2"
                            sx={{ textAlign: 'center', fontStyle: 'italic' }}
                          >
                            Something went wrong on our end. Please try again in
                            a moment.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : records.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={PROJECT_SUMMARY_COLUMNS.length}
                        sx={{ border: 0, height: defaultTableHeight }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2.5,
                            height: '100%',
                          }}
                        >
                          <Box
                            component="img"
                            src={search ? houseSearch : emptyMap}
                            alt=""
                            sx={{ width: '198px', height: 'auto' }}
                          />
                          <Typography
                            variant="body1"
                            sx={{ textAlign: 'center' }}
                          >
                            {search ? (
                              <>
                                No results found.
                                <br />
                                Please try a new search.
                              </>
                            ) : (
                              <>
                                Nothing to show yet, but you&apos;re on your
                                way!
                                <br />
                                Your projects will appear here as soon as one
                                reaches RFP.
                              </>
                            )}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.map((record) => (
                      <CustomerRFPRow
                        key={record.customerRFPId}
                        project={record}
                        search={search}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        {!isPending && !isError && records.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        )}
      </Box>
    </Surface>
  )
}

export interface ProjectsTableProps {
  records: ProjectSummary[]
  isPending: boolean
  isError: boolean
  sortBy: ClientPortfolioSortBy
  setSortBy: (newSortBy: ClientPortfolioSortBy) => void
  sortAsc: boolean
  setSortAsc: (newSortAsc: boolean) => void
  search?: string
  setSearch: (newSearch: string) => void
  page: number
  setPage: (newPage: number) => void
  totalPages: number
}
